import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "@/admin.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/wp-content/uploads/2022/08/logo.png";

const TABS = [
  { id: "form_submissions", label: "Website Forms", color: "#335341" },
  { id: "school_registrations", label: "School Registrations", color: "#03a9f4" },
  { id: "interest_registrations", label: "Register Interest", color: "#ff91ba" },
  { id: "get_involved", label: "Get Involved", color: "#ffc948" },
  { id: "newsletter_signups", label: "Newsletter Signups", color: "#87d0a0" },
];

const FIELD_LABELS = {
  school_name: "School", school_address: "Address", email: "Email", phone: "Phone", mp: "Local MP",
  election_date: "Election Date", name: "Name", postcode: "Postcode", position: "Position", school: "School",
  category: "Category", age: "Age", created_at: "Submitted",
};

const toCsv = (rows) => {
  if (!rows.length) return "";
  const cols = Object.keys(rows[0]).filter((k) => k !== "id");
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.map((c) => esc(FIELD_LABELS[c] || c)).join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("form_submissions");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/submissions`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/admin/login", { replace: true });
        } else {
          setError("Could not load submissions. Please try again.");
        }
      }
    })();
  }, [navigate]);

  const rows = useMemo(() => (data ? data[tab] || [] : []), [data, tab]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  const downloadCsv = () => {
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const deleteRow = async (entryId) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/submissions/${tab}/${entryId}`, { withCredentials: true });
      setData({ ...data, [tab]: rows.filter((r) => r.id !== entryId) });
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login", { replace: true });
      } else {
        setError("Could not delete that submission. Please try again.");
      }
    }
  };

  return (
    <div className="admin-body" data-testid="admin-dashboard">
      <header className="admin-topbar">
        <div className="admin-topbar-inner">
          <div>
            <img src={LOGO} alt="Children's Parliament" className="admin-topbar-logo" />
            <span className="admin-badge">Admin</span>
          </div>
          <div className="admin-topbar-actions">
            <a href="/" className="admin-view-site" data-testid="admin-view-site">View site</a>
            <button onClick={logout} className="admin-logout" data-testid="admin-logout-button">
              Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        <h1 className="admin-title">Submissions</h1>
        <p className="admin-sub">Everyone who has registered or signed up through the website forms.</p>

        {error && <div className="admin-error" data-testid="admin-error">{error}</div>}

        {data && (
          <>
            <div className="admin-stats">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`admin-stat ${tab === t.id ? "active" : ""}`}
                  style={{ backgroundColor: t.color }}
                >
                  <div className="admin-stat-count" data-testid={`admin-count-${t.id}`}>{(data[t.id] || []).length}</div>
                  <div className="admin-stat-label">{t.label}</div>
                </button>
              ))}
            </div>

            <div className="admin-panel">
              <div className="admin-panel-head">
                <h2 className="admin-panel-title">{TABS.find((t) => t.id === tab)?.label}</h2>
                <button onClick={downloadCsv} disabled={!rows.length} className="admin-csv" data-testid="admin-download-csv">
                  Download CSV
                </button>
              </div>
              {rows.length === 0 ? (
                <p className="admin-empty" data-testid="admin-empty">No submissions yet.</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table" data-testid="admin-table">
                    <thead>
                      <tr>
                        {Object.keys(rows[0]).filter((k) => k !== "id").map((k) => (
                          <th key={k}>{FIELD_LABELS[k] || k}</th>
                        ))}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          {Object.keys(r).filter((k) => k !== "id").map((k) => (
                            <td key={k}>{k === "created_at" ? fmtDate(r[k]) : r[k] || "—"}</td>
                          ))}
                          <td>
                            <button
                              onClick={() => deleteRow(r.id)}
                              className="admin-delete"
                              data-testid={`admin-delete-${r.id}`}
                              title="Delete this submission"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
